import type { GUID } from "#public"

/**
 * Generates a RFC 9562 version 7 UUID: 48-bit big-endian unix millisecond
 * timestamp, then 74 cryptographically random bits, with the version (7) and
 * variant (0b10) markers in place.
 *
 * @returns UUIDv7 as a 128-bit bigint
 *
 * @example
 * ```ts
 * import { GuidToString, UUID } from "@pdfking/tfunc"
 *
 * UUID() !== UUID() // true (always unique)
 * GuidToString(UUID()) // its base-36 string
 * ```
 */
export const UUID = (): GUID => {
  const bytes = new Uint8Array(10)
  crypto.getRandomValues(bytes)

  let random = 0n
  for (const byte of bytes) {
    random = (random << 8n) | BigInt(byte)
  }

  return (((BigInt(Date.now()) & 0xffffffffffffn) << 80n) | // unix_ts_ms
    (0x7n << 76n) | // ver
    (((random >> 62n) & 0xfffn) << 64n) | // rand_a
    (0b10n << 62n) | // var
    (random & 0x3fffffffffffffffn)) as GUID // rand_b
}
